/**
 * CompanyInviteService - Manages company invite code generation, validation, and redemption
 *
 * Boundary: Invite lifecycle only; does NOT handle user creation or email sending.
 * External dependencies: Firestore collection 'company-invites'
 */
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CompanyInvite } from '../models/company-invite';
import { FirestoreService } from './firestore.abstract.service';
import {
  Firestore as _Firestore,
  limit,
  query,
  where,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Company } from '../models/company';
import { User } from '../models/user';
import { LoggingService } from '@core/services/logging.service';
import { environment } from 'src/environments/environment';
import { CompanyMembershipService } from '@core/services/company-membership.service';

/**
 * Response from the /api/redeemInvite endpoint
 */
export interface RedeemInviteApiResult {
  success: boolean;
  companyId?: string;
  companyName?: string;
  role?: string;
  message?: string;
  error?: string;
  code?: string;
}

/**
 * CRUD operations for company invitation codes with crypto-secure generation.
 * Invite codes are 8-char hex strings (4 bytes entropy) with expiration dates.
 *
 * Dependencies:
 * - Router: Navigation context (inherited from base; unused in practice)
 * - LoggingService: Error handling
 * - FirestoreService<CompanyInvite>: Firestore CRUD base class
 *
 * Side effects:
 * - Firestore: company-invites collection (read/write)
 * - Properties: company, user (mutable state; TODO: clarify usage or remove)
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyInviteService extends FirestoreService<CompanyInvite> {
  router = inject(Router);
  private errorHandler = inject(LoggingService);
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  private auth = inject(Auth);
  private membershipService = inject(CompanyMembershipService);

  protected basePath: string = 'company-invites';
  public company?: Company | any;
  public user?: User | any;

  /**
   * Generates new invite code or updates existing one for company+email pair
   *
   * @param currentCompanyId - Company UID to associate with invite
   * @param email - Email address for invite recipient
   * @param expirationDate - ISO date string for invite expiry
   * @returns Promise<string> resolving to invite code (8-char hex uppercase)
   * - HTTP errors: N/A (Firestore errors propagated)
   * - State touched: Firestore company-invites (create or update)
   * - Idempotency: Safe to call multiple times for same email; updates existing invite expiration
   *
   * @example
   * const code = await service.generateOrUpdateInviteCode('acme', 'user@example.com', '2024-12-31');
   */
  async generateOrUpdateInviteCode(
    currentCompanyId: string,
    email: string,
    expirationDate: string,
  ): Promise<string> {
    // Check if there is already an invite code associated with this email
    const existingInviteData = await this.getInviteByCompanyAndEmail(
      currentCompanyId,
      email,
    );

    // The API honors an invite only from a verified issuer, and the rules
    // require the issuer to be the signed-in user (#874).
    const issuerUid = this.auth.currentUser?.uid;
    if (!issuerUid) {
      throw new Error('You must be signed in to send invites');
    }

    if (existingInviteData) {
      const existingInvite = new CompanyInvite(existingInviteData);
      // Update existing invite code with new expiration date; re-sending it
      // makes the current admin its issuer.
      existingInvite.expirationDate = expirationDate;
      existingInvite.createdBy = issuerUid;
      await this.update(existingInvite, existingInvite.uid);
      return existingInvite.uid;
    } else {
      // Generate a new invite code
      const inviteCode = this.generateUniqueCode();
      // Store the invite code along with its associated email address, company ID, and expiration date in the database
      const companyInvite = new CompanyInvite({
        uid: inviteCode,
        currentCompanyId,
        email,
        expirationDate,
        createdBy: issuerUid,
      });
      await this.create(companyInvite, inviteCode);
      return inviteCode;
    }
  }

  /**
   * Validates invite code by checking existence and expiration
   *
   * @param inviteCode - 8-char hex invite code to validate
   * @returns Promise<boolean> true if invite exists and not expired, false otherwise
   * - HTTP errors: Returns false on Firestore errors
   * - State touched: None (read-only Firestore query)
   * - Idempotency: Safe to call multiple times
   */
  async validateInviteCode(inviteCode: string): Promise<boolean> {
    try {
      const invite: CompanyInvite = await this.getDocument(inviteCode);
      if (invite && invite.expirationDate) {
        const expirationDate = new Date(invite.expirationDate);
        return expirationDate > new Date();
      } else {
        return false;
      }
    } catch (error) {
      // Handle error if necessary
      this.errorHandler.handleError(error);
      return false;
    }
  }

  /**
   * Redeems an invite code via the secure backend API endpoint.
   *
   * Calls POST /api/redeemInvite with a Firebase auth token, which:
   * - Validates the invite (existence, expiration, email match)
   * - Adds the authenticated user to the invited company
   * - Assigns the per-company role specified on the invite
   * - Marks the invite as used
   *
   * @param inviteCode - 8-char hex invite code to redeem
   * @returns Promise<RedeemInviteApiResult> result with companyId, companyName, and role
   * - Requires authenticated Firebase user
   * - HTTP errors: Propagated from API call
   * - State touched: Firestore users doc (companies array, currentCompanyId, companyRoles)
   */
  async redeemInviteViaApi(inviteCode: string): Promise<RedeemInviteApiResult> {
    if (!this.auth.currentUser) {
      throw new Error('User must be authenticated to redeem an invite');
    }
    // Bearer token plus the Functions key: redeemInvite is authLevel
    // "function", so a request without the key never reaches the handler.
    const headers = await this.membershipService.authHeaders();
    return firstValueFrom(
      this.http.post<RedeemInviteApiResult>(
        `${environment.app_api_url}/api/redeemInvite`,
        { inviteCode },
        { headers },
      ),
    );
  }

  /**
   * Generate a unique invite code
   * @returns A unique invite code (e.g., a random alphanumeric string)
   */
  private generateUniqueCode(): string {
    // Use cryptographically secure random values for invite codes (security-critical)
    // 4 bytes = 32 bits entropy, hex encoding = 8 chars
    const randomBytes = new Uint8Array(4);
    if (isPlatformBrowser(this.platformId)) {
      window.crypto.getRandomValues(randomBytes);
    } else {
      for (let i = 0; i < randomBytes.length; i++) {
        randomBytes[i] = Math.floor(Math.random() * 256);
      }
    }
    // Convert to hex string (8 chars for readability, 4 bytes of entropy)
    return Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }

  /**
   * Queries invite by company ID and email (used for duplicate detection)
   *
   * @param currentCompanyId - Company UID
   * @param email - Invite recipient email
   * @returns Promise<CompanyInvite | undefined> invite if found, else undefined
   * - HTTP errors: Propagated from Firestore query
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  async getInviteByCompanyAndEmail(
    currentCompanyId: string,
    email: string,
  ): Promise<CompanyInvite | undefined> {
    // Define a custom query function using Firestore query chaining methods
    const customQueryFn = (collectionRef: any) => {
      return query(
        collectionRef,
        where('currentCompanyId', '==', currentCompanyId),
        where('email', '==', email),
        limit(1),
      );
    };

    // Retrieve the query snapshot using custom query function
    const querySnapshot = await this.getCollection$(customQueryFn);

    // Check if the query snapshot is not empty
    if (!querySnapshot.empty) {
      // Return the data of the first document as CompanyInvite
      return querySnapshot.docs[0].data() as CompanyInvite;
    }

    // Return undefined if no matching invite is found
    return undefined;
  }

  /**
   * Marks invite as unsubscribed (user opt-out)
   *
   * @param inviteCode - Invite code to unsubscribe
   * @returns Promise<void> resolves when Firestore update completes
   * - HTTP errors: Throws if invite not found
   * - State touched: Firestore company-invites (set unsubscribed flag)
   * - Idempotency: Safe to call multiple times; idempotent flag set
   */
  async unsubscribeInvite(inviteCode: string): Promise<void> {
    // Fetch the invite model
    const invite = await this.getDocument(inviteCode);
    if (invite) {
      // Only the opt-out flag: firestore.rules lets the invitee change this
      // one field and nothing else on the invite (#874).
      await this.update({ unsubscribed: true }, inviteCode);
    } else {
      throw new Error('Invite not found.');
    }
  }

  /**
   * Marks invite as invalidated (admin action)
   *
   * @param inviteCode - Invite code to invalidate
   * @returns Promise<CompanyInvite> updated invite object
   * - HTTP errors: Throws if invite not found
   * - State touched: Firestore company-invites (set invalidated flag)
   * - Idempotency: Safe to call multiple times; idempotent flag set
   */
  async invalidateInvite(inviteCode: string): Promise<CompanyInvite> {
    // Fetch the invite model
    const invite = await this.getDocument(inviteCode);
    if (invite) {
      invite.invalidated = true;
      return await this.update(invite, inviteCode);
    } else {
      throw new Error('Invite not found.');
    }
  }

  /**
   * Deletes an invite from Firestore
   *
   * @param uid - Invite code (document ID) to delete
   * @returns Promise<boolean> true if deleted, false on error
   * - HTTP errors: Logged and swallowed (returns false)
   * - State touched: Firestore company-invites (delete operation)
   * - Idempotency: Safe to call multiple times; subsequent calls no-op
   */
  async deleteInvite(uid: string): Promise<boolean> {
    try {
      await this.delete(uid);
      return true;
    } catch (error) {
      this.errorHandler.handleError(error);
      return false;
    }
  }
}
